import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Box,
  Stack,
} from "@mui/material";
import MainCard from "ui-component/cards/MainCard";

const positiveEvents = [
  { name: "First Payment Completed", score: "+25" },
  { name: "On-Time Payment", score: "+30" },
  { name: "Early Bird Payment", score: "+40" },
  { name: "Partial Payment Before Due Date", score: "+10" },
  { name: "Full Recovery", score: "+12" },
  { name: "3-Month Consistency Streak", score: "+50" },
  { name: "6-Month Consistency Streak", score: "+100" },
  { name: "Advance Payment", score: "+25 / +75" },
];

const negativeEvents = [
  { name: "Late Payment", score: "-30" },
  { name: "Very Late Payment", score: "-50" },
  { name: "Critical Payment Miss (2 Months)", score: "-40" },
  { name: "Severe Payment Miss (3+ Months)", score: "-75" },
];

const badges = [
  {
    title: "🏅 On-Time Payer",
    description:
      "Awarded to tenants maintaining 3-6 months of consecutive On-Time or Early payments.",
  },
  {
    title: "💰 Advance Planner",
    description:
      "Awarded when rent is paid 1-3 months in advance, showing strong financial planning.",
  },
  {
    title: "✅ Recovery Compliant",
    description:
      "Awarded when missed payments are cleared before breach deadlines.",
  },
  {
    title: "🛡️ Low-Risk Tenant",
    description:
      "Awarded to tenants with no severe misses, critical misses, or very late payments in the last 12 months.",
  },
];

const BadgeSystemOverview = () => {
  return (
    <MainCard title="Badge System Overview">
      <Grid container spacing={3}>
        {/* Overview */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h2" gutterBottom>
                Tenant Trust Score System
              </Typography>

              <Typography variant="body1">
                Reanent's Trust Score system evaluates tenant payment behavior
                and automatically rewards positive actions while penalizing
                payment delays and long-term non-payment.
              </Typography>

              <Box mt={2}>
                <Chip
                  label="8 Positive Rating Events"
                  color="success"
                  sx={{ mr: 1 }}
                />
                <Chip
                  label="4 Negative Rating Events"
                  color="error"
                  sx={{ mr: 1 }}
                />
                <Chip
                  label="4 Tenant Badges"
                  color="primary"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Positive Events */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h3" gutterBottom color="success.main">
                Positive Rating Events
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1.5}>
                {positiveEvents.map((item) => (
                  <Box
                    key={item.name}
                    display="flex"
                    justifyContent="space-between"
                  >
                    <Typography>{item.name}</Typography>
                    <Chip
                      label={item.score}
                      color="success"
                      size="small"
                    />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Negative Events */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h3" gutterBottom color="error.main">
                Negative Rating Events
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1.5}>
                {negativeEvents.map((item) => (
                  <Box
                    key={item.name}
                    display="flex"
                    justifyContent="space-between"
                  >
                    <Typography>{item.name}</Typography>
                    <Chip
                      label={item.score}
                      color="error"
                      size="small"
                    />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Badges */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h3" gutterBottom>
                Tenant Badges
              </Typography>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2}>
                {badges.map((badge) => (
                  <Grid item xs={12} md={6} key={badge.title}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h4" gutterBottom>
                          {badge.title}
                        </Typography>

                        <Typography variant="body2">
                          {badge.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Core Rules */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h3" gutterBottom>
                Core System Rules
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <ul>
                <li>24-hour grace period after due date.</li>
                <li>Only one positive event can apply per billing cycle.</li>
                <li>Only one negative event can apply per billing cycle.</li>
                <li>Highest positive event takes priority.</li>
                <li>Worst negative event takes priority.</li>
                <li>Partial payments do not close a billing cycle.</li>
                <li>Advance payments automatically close future months.</li>
                <li>Streaks are evaluated monthly.</li>
                <li>Late or missed payments break streaks.</li>
                <li>Recovery can prevent future penalties but cannot remove already-applied penalties.</li>
              </ul>
            </CardContent>
          </Card>
        </Grid>

        {/* Badge Decision Summary */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h3" gutterBottom>
                Badge Decision Summary
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Typography>
                <strong>On-Time Payer:</strong> 3+ months of Early/On-Time
                payments.
              </Typography>

              <Typography mt={1}>
                <strong>Advance Planner:</strong> Rent paid 1-3 months ahead.
              </Typography>

              <Typography mt={1}>
                <strong>Recovery Compliant:</strong> Cleared pending dues before
                breach deadlines.
              </Typography>

              <Typography mt={1}>
                <strong>Low-Risk Tenant:</strong> No Critical Miss, Severe Miss,
                or Very Late Payment events in the last 12 months.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </MainCard>
  );
};

export default BadgeSystemOverview;